import { useEffect, useState, useCallback } from "react";
import { useTour } from "@/lib/tour-store";
import { TOUR_STEPS } from "@/lib/tour-steps";
import { supabase, getCurrentTenant } from "@/lib/session";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const RESOLVE_TIMEOUT_MS = 1500;
const TOOLTIP_W = 360;
const TOOLTIP_GAP = 14;

function getRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function normalizeHash(h: string): string {
  // Always returns the path portion without leading "#"
  return (h || "").replace(/^#/, "");
}

function stepRoutePath(stepRoute: string): string {
  // tour-steps routes are written like "/#/dashboards/ae" — strip "/#" prefix
  return stepRoute.startsWith("/#") ? stepRoute.slice(2) : stepRoute;
}

function hashMatchesStep(stepRoute: string): boolean {
  const current = normalizeHash(window.location.hash);
  const target = stepRoutePath(stepRoute);
  // Allow trailing slashes / query strings to count as match
  const cur = current.split("?")[0].replace(/\/$/, "");
  const tgt = target.split("?")[0].replace(/\/$/, "");
  return cur === tgt;
}

function placeTooltip(targetRect: Rect | null): { top: number; left: number; placement: "below" | "above" | "center" } {
  if (!targetRect) {
    const top = Math.max(40, window.innerHeight / 2 - 120);
    const left = Math.max(20, window.innerWidth / 2 - TOOLTIP_W / 2);
    return { top, left, placement: "center" };
  }
  const spaceBelow = window.innerHeight - (targetRect.top + targetRect.height);
  const placeBelow = spaceBelow > 220 || targetRect.top < 220;
  const top = placeBelow
    ? targetRect.top + targetRect.height + TOOLTIP_GAP
    : Math.max(20, targetRect.top - 220 - TOOLTIP_GAP);
  const rawLeft = targetRect.left + targetRect.width / 2 - TOOLTIP_W / 2;
  const left = clamp(rawLeft, 20, window.innerWidth - TOOLTIP_W - 20);
  return { top, left, placement: placeBelow ? "below" : "above" };
}

async function runReseed(): Promise<string> {
  try {
    const { error } = await supabase().rpc("reseed_demo_tenant");
    if (error) return `Reset failed: ${error.message}`;
    return "Demo data reset. Ready for next session.";
  } catch (e: any) {
    return `Reset failed: ${e?.message || "unknown error"}`;
  }
}

/** Floating pill shown when the tour is paused (Esc pressed). */
function ResumePill({ onResume }: { onResume: () => void }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 2147483646,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#0B0B0F",
        color: "#FFFFFF",
        borderRadius: 999,
        padding: "10px 18px",
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 4px 20px rgba(15,23,42,0.35)",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
      role="button"
      tabIndex={0}
      onClick={onResume}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onResume(); }}
      aria-label="Resume tour"
    >
      Resume tour
    </div>
  );
}

export function TourOverlay() {
  const tour = useTour();
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [missing, setMissing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [resetStatus, setResetStatus] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const step = !paused && tour.isActive && tour.currentStop >= 0 ? TOUR_STEPS[tour.currentStop] : null;
  const isResetStop = step?.isResetStop === true;
  const onStepRoute = step ? hashMatchesStep(step.route) : true;

  // ?notour=1 escape hatch — clears any saved tour state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.search.includes("notour=1") || window.location.hash.includes("notour=1")) {
      try {
        window.sessionStorage.removeItem("aip:tour:isActive");
        window.sessionStorage.removeItem("aip:tour:currentStop");
        window.sessionStorage.removeItem("aip:tour:completed");
      } catch { /* ignore */ }
      if (tour.isActive) tour.exitTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: if a saved active tour exists but the current hash doesn't match
  // the saved step's route, pause immediately to avoid bleeding a stale popover
  // onto an unrelated page after a reload or manual navigation.
  useEffect(() => {
    if (!tour.isActive || tour.currentStop < 0) return;
    const saved = TOUR_STEPS[tour.currentStop];
    if (saved && !hashMatchesStep(saved.route)) {
      setPaused(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for manual navigation. If hash changes to a route that doesn't
  // match the current step, pause the tour. Tour-driven nav lands on the
  // step route itself, so this only triggers on user-initiated nav.
  useEffect(() => {
    if (!tour.isActive) return;
    const onHashChange = () => {
      if (tour.currentStop < 0) return;
      const cur = TOUR_STEPS[tour.currentStop];
      if (cur && !hashMatchesStep(cur.route)) {
        setPaused(true);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [tour.isActive, tour.currentStop]);

  // Resolve the target element and update its rect on every layout pulse.
  useEffect(() => {
    if (!step) {
      setTargetRect(null);
      setMissing(false);
      return;
    }

    let cancelled = false;
    let timer: number | null = null;
    let raf: number | null = null;
    const start = Date.now();

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(step.targetSelector);
      if (el) {
        setMissing(false);
        const rect = getRect(el);
        setTargetRect(rect);
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        } catch {
          /* older browsers */
        }
        raf = window.requestAnimationFrame(syncOnly);
        return;
      }
      if (Date.now() - start > RESOLVE_TIMEOUT_MS) {
        setMissing(true);
        setTargetRect(null);
        return;
      }
      timer = window.setTimeout(tick, 100);
    };

    const syncOnly = () => {
      if (cancelled) return;
      const el = document.querySelector(step.targetSelector);
      if (el) {
        setTargetRect(getRect(el));
      }
      raf = window.requestAnimationFrame(syncOnly);
    };

    timer = window.setTimeout(tick, 400);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [step?.id, step?.targetSelector, tour.currentStop]);

  // Keyboard handlers — Right/Enter=next, Left=back, Esc=pause.
  useEffect(() => {
    if (!tour.isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (paused) {
        if (e.key === "Escape") {
          e.preventDefault();
          setPaused(false);
        }
        return;
      }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        tour.nextStop();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        tour.prevStop();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setPaused(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tour.isActive, tour.nextStop, tour.prevStop, paused]);

  // Reset status clears on stop change
  useEffect(() => {
    setResetStatus(null);
  }, [tour.currentStop]);

  const handleReset = useCallback(async () => {
    setResetting(true);
    const msg = await runReseed();
    setResetting(false);
    setResetStatus(msg);
  }, []);

  // Show resume pill when paused
  if (paused && tour.isActive) {
    return <ResumePill onResume={() => setPaused(false)} />;
  }

  if (!tour.isActive || !step) return null;

  // Last-line defense: if we're not on the step's route, render nothing
  // (the auto-pause hook will set paused=true on the next tick).
  if (!onStepRoute) return null;

  const tooltipPos = placeTooltip(targetRect);
  const isLast = tour.currentStop === tour.total - 1;

  return (
    <div
      role="dialog"
      aria-label="Guided tour"
      style={{ position: "fixed", inset: 0, zIndex: 2147483646, pointerEvents: "none" }}
    >
      {/* Dim ring around target */}
      {targetRect && !missing && (
        <div
          style={{
            position: "fixed",
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            borderRadius: 8,
            boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)",
            pointerEvents: "none",
            transition: "top 200ms ease, left 200ms ease, width 200ms ease, height 200ms ease",
          }}
        />
      )}

      {/* Full dim if no target */}
      {(missing || !targetRect) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Backdrop — clicking outside the tooltip pauses the tour. */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "auto",
          background: "transparent",
        }}
        onClick={(e) => {
          e.stopPropagation();
          setPaused(true);
        }}
      />

      {/* Tooltip card — top-left on reset stop, dynamic otherwise */}
      <div
        data-tour="tour-overlay-card"
        style={{
          position: "fixed",
          top: isResetStop ? 24 : tooltipPos.top,
          left: isResetStop ? 24 : tooltipPos.left,
          width: TOOLTIP_W,
          background: "#FFFFFF",
          color: "#0B0B0F",
          border: "1px solid #E5E5E7",
          borderRadius: 10,
          boxShadow: "0 18px 40px rgba(15,23,42,0.30)",
          padding: 18,
          pointerEvents: "auto",
          fontFamily: "inherit",
          transition: isResetStop ? "none" : "top 200ms ease, left 200ms ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 40,
              height: 22,
              padding: "0 8px",
              borderRadius: 999,
              background: "#0B0B0F",
              color: "#FFFFFF",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            {tour.currentStop + 1} / {tour.total}
          </span>
          <button
            type="button"
            onClick={() => setPaused(true)}
            style={{
              border: "1px solid #E5E5E7",
              background: "#FFFFFF",
              color: "#525258",
              borderRadius: 6,
              padding: "4px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Pause (Esc)
          </button>
        </div>

        {/* Title */}
        <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, marginBottom: 8 }}>
          {step.title}
        </div>

        {/* Narration */}
        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#1F2937", marginBottom: 10 }}>
          {step.narration}
        </div>

        {/* Why it matters */}
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            color: "#525258",
            fontStyle: "italic",
            paddingLeft: 10,
            borderLeft: "2px solid #DC2626",
            marginBottom: 14,
          }}
        >
          Why it matters — {step.whyItMatters}
        </div>

        {/* Missing target warning */}
        {missing && (
          <div
            style={{
              fontSize: 11,
              color: "#92400E",
              background: "#FEF3C7",
              border: "1px solid #FDE68A",
              borderRadius: 6,
              padding: "6px 8px",
              marginBottom: 12,
            }}
          >
            Target element not found on this screen yet. Skip ahead, or wait for the page to finish loading.
          </div>
        )}

        {/* Reset block — only on final stop */}
        {isResetStop && (
          <div style={{ marginBottom: 14 }}>
            <button
              type="button"
              data-tour="demo-reset-button"
              onClick={handleReset}
              disabled={resetting}
              style={{
                width: "100%",
                border: "1px solid #DC2626",
                background: resetting ? "#FEF2F2" : "#DC2626",
                color: resetting ? "#DC2626" : "#FFFFFF",
                borderRadius: 6,
                padding: "9px 14px",
                fontSize: 13,
                fontWeight: 600,
                cursor: resetting ? "default" : "pointer",
                transition: "background 150ms ease",
              }}
            >
              {resetting ? "Resetting demo data..." : "Reset demo for next session"}
            </button>
            {resetStatus && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: resetStatus.startsWith("Reset failed") ? "#DC2626" : "#166534",
                  background: resetStatus.startsWith("Reset failed") ? "#FEF2F2" : "#F0FDF4",
                  border: `1px solid ${resetStatus.startsWith("Reset failed") ? "#FECACA" : "#BBF7D0"}`,
                  borderRadius: 6,
                  padding: "6px 8px",
                }}
              >
                {resetStatus}
              </div>
            )}
          </div>
        )}

        {/* Nav buttons */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <button
            type="button"
            onClick={() => tour.prevStop()}
            disabled={tour.currentStop === 0}
            style={{
              border: "1px solid #E5E5E7",
              background: "#FFFFFF",
              color: tour.currentStop === 0 ? "#8E8E94" : "#0B0B0F",
              borderRadius: 6,
              padding: "8px 12px",
              fontSize: 12,
              cursor: tour.currentStop === 0 ? "default" : "pointer",
            }}
          >
            Back
          </button>
          <div style={{ fontSize: 11, color: "#8E8E94" }}>
            ← / → · Esc to pause
          </div>
          {!isLast ? (
            <button
              type="button"
              onClick={() => tour.nextStop()}
              style={{
                border: "1px solid #0B0B0F",
                background: "#0B0B0F",
                color: "#FFFFFF",
                borderRadius: 6,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => tour.exitTour()}
              style={{
                border: "1px solid #6B6B70",
                background: "#FFFFFF",
                color: "#6B6B70",
                borderRadius: 6,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Exit tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
